/**
 * SupportDialog - the in-app "Help & feedback" modal.
 *
 * The user picks a kind (support / feature / bug) and writes a message; it
 * reaches the admin by email and Telegram. Nothing is stored on our side, so
 * the dialog only reports success when the API confirms delivery, and offers
 * the support address when it does not.
 *
 * Design: docs/superpowers/specs/2026-08-03-in-app-help-and-feedback-design.md
 */

import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { authHeaders } from '../../lib/authHeaders';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8001';

const KINDS = [
  { value: 'support', label: 'Support', hint: 'Something is not working for me' },
  { value: 'feature', label: 'Feature', hint: 'I have an idea for the product' },
  { value: 'bug', label: 'Bug', hint: 'I found something broken' },
] as const;

type Kind = (typeof KINDS)[number]['value'];

const MIN_LENGTH = 10;
const MAX_LENGTH = 4000;

interface SupportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SupportDialog({ isOpen, onClose }: SupportDialogProps) {
  const location = useLocation();

  const [kind, setKind] = useState<Kind>('support');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'form' | 'sending' | 'sent' | 'error'>('form');
  const [error, setError] = useState<string | null>(null);

  // A fresh dialog every time it opens: a previous success or error must not
  // greet the next person who clicks the button.
  useEffect(() => {
    if (isOpen) {
      setKind('support');
      setMessage('');
      setStatus('form');
      setError(null);
    }
  }, [isOpen]);

  const canSend = message.trim().length >= MIN_LENGTH && status !== 'sending';

  const send = async () => {
    setStatus('sending');
    setError(null);
    try {
      await axios.post(
        `${API_BASE}/api/v1/support/message`,
        { kind, message: message.trim(), page: location.pathname },
        { headers: authHeaders() }
      );
      setStatus('sent');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not send your message. Please try again.'
      );
      setStatus('error');
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/55" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <Dialog.Panel className="gd-sup">
                <div className="gd-sup-bar">
                  <Dialog.Title className="gd-sup-title">Help &amp; feedback</Dialog.Title>
                  <button
                    type="button"
                    className="gd-sup-x"
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon />
                  </button>
                </div>

                {status === 'sent' ? (
                  <div className="gd-sup-body">
                    <div className="gd-sup-done">
                      <b>Thanks. Your message is on its way.</b>
                      <p>We reply within 2 business days.</p>
                    </div>
                    <div className="gd-sup-actions">
                      <button type="button" className="gd-sup-btn primary" onClick={onClose}>
                        Close
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="gd-sup-body">
                    <div className="gd-sup-label">What is this about?</div>
                    <div className="gd-sup-kinds" role="radiogroup" aria-label="Message kind">
                      {KINDS.map((k) => (
                        <button
                          key={k.value}
                          type="button"
                          role="radio"
                          aria-checked={kind === k.value}
                          className={`gd-sup-kind ${kind === k.value ? 'on' : ''}`}
                          onClick={() => setKind(k.value)}
                        >
                          <b>{k.label}</b>
                          <span>{k.hint}</span>
                        </button>
                      ))}
                    </div>

                    <label className="gd-sup-label" htmlFor="gd-sup-msg">
                      Your message
                    </label>
                    <textarea
                      id="gd-sup-msg"
                      className="gd-sup-text"
                      rows={6}
                      maxLength={MAX_LENGTH}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={
                        kind === 'feature'
                          ? 'What would you like the product to do?'
                          : 'What happened, and what did you expect instead?'
                      }
                    />

                    <div className="gd-sup-meta">
                      <span>
                        {message.trim().length}/{MAX_LENGTH}
                      </span>
                    </div>

                    {status === 'error' && error && (
                      <div className="gd-sup-err" role="alert">
                        {error}{' '}
                        <a href="mailto:support@getdecision.ai">support@getdecision.ai</a>
                      </div>
                    )}

                    <div className="gd-sup-actions">
                      <button type="button" className="gd-sup-btn" onClick={onClose}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="gd-sup-btn primary"
                        onClick={send}
                        disabled={!canSend}
                      >
                        {status === 'sending' ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
